import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Eye, EyeOff, Pencil, Star } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const emptyForm = {
  title_fr: "", title_en: "",
  description_fr: "", description_en: "",
  image_url: "",
  goal_amount: 10000,
  currency: "EUR",
  deadline: "",
  featured: true,
  order: 0,
  published: true,
};

const CampaignsManager = ({ tAdmin, lang }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/campaigns");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        goal_amount: parseFloat(form.goal_amount) || 0,
        order: parseInt(form.order, 10) || 0,
        deadline: form.deadline || null,
      };
      if (editId) {
        await api.put(`/admin/campaigns/${editId}`, payload);
        toast.success(tAdmin.campaigns.updated);
      } else {
        await api.post("/admin/campaigns", payload);
        toast.success(tAdmin.campaigns.created);
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditId(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({
      title_fr: c.title_fr || "", title_en: c.title_en || "",
      description_fr: c.description_fr || "", description_en: c.description_en || "",
      image_url: c.image_url || "",
      goal_amount: c.goal_amount || 0,
      currency: c.currency || "EUR",
      deadline: c.deadline || "",
      featured: !!c.featured,
      order: c.order || 0,
      published: !!c.published,
    });
    setShowForm(true);
  };

  const togglePublish = async (c) => {
    try {
      await api.put(`/admin/campaigns/${c.id}`, { published: !c.published });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(tAdmin.campaigns.confirmDelete.replace("{title}", c.title_fr))) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success(tAdmin.campaigns.deleted);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const fmt = (a, ccy) => {
    try {
      return new Intl.NumberFormat(lang === "en" ? "en-US" : "fr-FR", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(a);
    } catch (_) { return `${a} ${ccy}`; }
  };

  return (
    <div data-testid="campaigns-manager">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#002B49] font-semibold">{tAdmin.campaigns.title}</h2>
          <p className="text-sm text-[#64748B]">{tAdmin.campaigns.subtitle}</p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"
            data-testid="campaigns-add-btn"
          >
            <Plus size={14} /> {tAdmin.campaigns.addCampaign}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 p-6 mb-6" data-testid="campaigns-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={tAdmin.campaigns.fTitleFr}><input required value={form.title_fr} onChange={e => setForm({...form, title_fr: e.target.value})} className="form-input" data-testid="campaign-title-fr" /></Field>
            <Field label={tAdmin.campaigns.fTitleEn}><input required value={form.title_en} onChange={e => setForm({...form, title_en: e.target.value})} className="form-input" data-testid="campaign-title-en" /></Field>
            <Field label={tAdmin.campaigns.fImage} className="sm:col-span-2"><input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="form-input" data-testid="campaign-image" /></Field>
            <Field label={tAdmin.campaigns.fGoal}><input required type="number" step="100" min="100" value={form.goal_amount} onChange={e => setForm({...form, goal_amount: e.target.value})} className="form-input" data-testid="campaign-goal" /></Field>
            <Field label={tAdmin.campaigns.fCurrency}>
              <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} className="form-input">
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </Field>
            <Field label={tAdmin.campaigns.fDeadline}><input type="date" value={form.deadline || ""} onChange={e => setForm({...form, deadline: e.target.value})} className="form-input" data-testid="campaign-deadline" /></Field>
            <Field label={tAdmin.campaigns.fOrder}><input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} className="form-input" data-testid="campaign-order" /></Field>
            <Field label={tAdmin.campaigns.fFeatured}>
              <select value={form.featured ? "1" : "0"} onChange={e => setForm({...form, featured: e.target.value === "1"})} className="form-input" data-testid="campaign-featured">
                <option value="1">{tAdmin.campaigns.yes}</option>
                <option value="0">{tAdmin.campaigns.no}</option>
              </select>
            </Field>
            <Field label={tAdmin.campaigns.fPublished}>
              <select value={form.published ? "1" : "0"} onChange={e => setForm({...form, published: e.target.value === "1"})} className="form-input">
                <option value="1">{tAdmin.campaigns.yes}</option>
                <option value="0">{tAdmin.campaigns.no}</option>
              </select>
            </Field>
            <Field label={tAdmin.campaigns.fDescFr} className="sm:col-span-2"><textarea required rows="4" value={form.description_fr} onChange={e => setForm({...form, description_fr: e.target.value})} className="form-input" data-testid="campaign-desc-fr" /></Field>
            <Field label={tAdmin.campaigns.fDescEn} className="sm:col-span-2"><textarea required rows="4" value={form.description_en} onChange={e => setForm({...form, description_en: e.target.value})} className="form-input" data-testid="campaign-desc-en" /></Field>
          </div>
          <div className="flex gap-2 mt-5">
            <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium" data-testid="campaign-save"><Save size={14} /> {editId ? tAdmin.campaigns.save : tAdmin.campaigns.create}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-black/10 text-[#64748B] text-sm"><X size={14} /> {tAdmin.campaigns.cancel}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#64748B]">…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#64748B]" data-testid="campaigns-empty">{tAdmin.campaigns.empty}</div>
        ) : (
          <ul className="divide-y divide-black/5">
            {items.map((c, i) => (
              <li key={c.id} className="p-5 flex items-center gap-4 flex-wrap" data-testid={`campaign-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#002B49]">{c.title_fr}</span>
                    {c.featured && <span className="text-[10px] uppercase tracking-wider text-[#C5A059] bg-[#FDF8EE] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Star size={9} /> Featured</span>}
                    {!c.published && <span className="text-[10px] uppercase tracking-wider text-[#64748B] bg-black/5 px-2 py-0.5 rounded-full">Draft</span>}
                  </div>
                  <div className="text-xs text-[#64748B] mt-1">
                    {tAdmin.campaigns.raised}: <span className="font-semibold text-[#002B49]">{fmt(c.raised_amount, c.currency)}</span> · {tAdmin.campaigns.goal}: {fmt(c.goal_amount, c.currency)} · {c.percentage}% · {c.donor_count} {tAdmin.campaigns.donors}
                  </div>
                  <div className="h-1.5 bg-[#F3F0EA] rounded-full overflow-hidden mt-2 max-w-md">
                    <div className="h-full bg-gradient-to-r from-[#002B49] to-[#C5A059] rounded-full" style={{ width: `${Math.min(c.percentage, 100)}%` }} />
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => togglePublish(c)} className="p-2 rounded-md hover:bg-black/5 text-[#64748B]" data-testid={`campaign-toggle-${i}`}>{c.published ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                  <button onClick={() => startEdit(c)} className="p-2 rounded-md hover:bg-black/5 text-[#64748B]" data-testid={`campaign-edit-${i}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(c)} className="p-2 rounded-md hover:bg-red-50 text-red-600" data-testid={`campaign-delete-${i}`}><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <style>{`
        .form-input { width:100%; margin-top:.4rem; padding:.6rem .85rem; border-radius:.45rem; border:1px solid #E5E7EB; outline:none; transition:border-color .2s, box-shadow .2s; font-size:.9rem; color:#0A192F; background:#fff; }
        .form-input:focus { border-color:#002B49; box-shadow:0 0 0 3px rgba(0,43,73,.1); }
      `}</style>
    </div>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] font-medium text-[#64748B] uppercase tracking-wider">{label}</span>
    {children}
  </label>
);

export default CampaignsManager;
